/*
 * Stats counter — numbers count up from 0 when the section scrolls into view (Awwwards-style).
 *
 * Values aren't plain numbers ("200+", "10k+", "5*", or "1,000+", "4.9*"), so each is parsed into a
 * prefix / numeric part / suffix; only the number animates and the surrounding text + formatting are
 * preserved. The final value is already in the HTML, so with no JS / reduced motion it just shows as-is.
 *
 * Plays once when hit (onEnter, not scrubbed). Requires gsap + ScrollTrigger (loaded in <head>).
 * <wp-stats-counter> (display: contents) wraps the [data-stats] section; built in connectedCallback
 * inside gsap.matchMedia() (motion-allowed only) and reverted — final values restored — on disconnect.
 */
(function () {
  if (!window.gsap || !window.ScrollTrigger || customElements.get('wp-stats-counter')) return;

  const parse = (text) => {
    const m = (text || '').trim().match(/^(\D*)([\d.,]+)(.*)$/);
    if (!m) return null;
    const [, prefix, numStr, suffix] = m;
    const target = parseFloat(numStr.replace(/,/g, ''));
    if (isNaN(target)) return null;
    return {
      prefix,
      suffix,
      target,
      decimals: (numStr.split('.')[1] || '').length,
      hasComma: numStr.includes(','),
    };
  };

  const format = (val, info) => {
    let n = info.decimals ? val.toFixed(info.decimals) : Math.round(val).toString();
    if (info.hasComma) {
      n = Number(n).toLocaleString('en-US', {
        minimumFractionDigits: info.decimals,
        maximumFractionDigits: info.decimals,
      });
    }
    return info.prefix + n + info.suffix;
  };

  const build = (section) => {
    const nums = [...section.querySelectorAll('[data-stat-number]')]
      .map((el) => {
        const info = parse(el.textContent);
        return info ? { el, info, original: el.textContent } : null;
      })
      .filter(Boolean);
    if (!nums.length) return;

    // Reset to the start value while still off-screen (no flash of the final value).
    nums.forEach(({ el, info }) => {
      el.textContent = format(0, info);
    });

    // Created later (onEnter), so outside matchMedia's context — killed in the cleanup below.
    const tweens = [];
    ScrollTrigger.create({
      trigger: section,
      start: 'top 80%',
      once: true,
      onEnter: () => {
        nums.forEach(({ el, info }, i) => {
          const proxy = { v: 0 };
          tweens.push(
            gsap.to(proxy, {
              v: info.target,
              duration: 1.6,
              ease: 'power2.out',
              delay: i * 0.08,
              onUpdate: () => {
                el.textContent = format(proxy.v, info);
              },
            }),
          );
        });
      },
    });

    // Put the rendered values back (matchMedia can't see textContent writes).
    return () => {
      tweens.forEach((tween) => tween.kill());
      nums.forEach(({ el, original }) => {
        el.textContent = original;
      });
    };
  };

  class WpStatsCounter extends HTMLElement {
    connectedCallback() {
      const section = this.querySelector('[data-stats]');
      if (!section) return;
      // Reduced motion: no handler runs, so the final values stay exactly as rendered.
      this.mm = gsap.matchMedia();
      this.mm.add('(prefers-reduced-motion: no-preference)', () => build(section));
    }

    disconnectedCallback() {
      if (this.mm) this.mm.revert();
      this.mm = null;
    }
  }

  customElements.define('wp-stats-counter', WpStatsCounter);
})();
